import { Back, Power1, TweenMax } from 'gsap';
import Viewport from 'pixi-viewport';
import { Rectangle, Sprite } from 'pixi.js';
import Core from '../../../Core/Core';
import { app } from '../../../index';
import { waitForMs } from '../../../Utils/AsyncUtils';
import { LeaderboardFriendTab } from './LeaderboardFriendTab';
import { InviteBox } from './InviteBox';
import { createOutlineText, createPixiText } from '../../../Utils/Utils';
import { PopupPage } from '../../Shared/PopupPage';
import { IInvite } from '../../../State/socialHelpers';

export interface ILeaderBoardPlayer {
    name: string;
    stars: number;
    isUser: boolean;
    url: string;
    id: string;
}

/**
 * the leaderboard tab of the socialhubpopup
 * shows the friends of the player and a comparison of their progress
 */

export class LeaderBoard extends PopupPage {
    private disclamer: PIXI.Text;
    private disclamerEmoji: PIXI.Sprite;
    private hightlight: PIXI.Sprite;
    private weeklyPlayers: ILeaderBoardPlayer[];
    private players: ILeaderBoardPlayer[];
    private openInvitations: IInvite[];
    private scrollContainer: PIXI.Container;
    private lBContainer: Viewport;
    private containerWidth: number;
    private containerHeight: number;
    private emojiplus: PIXI.Sprite;
    private together: PIXI.Text;
    private tabHeight: number;
    private playerTabs: LeaderboardFriendTab[] = [];

    constructor(
        width: number,
        height: number,
        weeklyPlayers: ILeaderBoardPlayer[],
        players: ILeaderBoardPlayer[],
        pending: IInvite[],
        canSendGifts: boolean
    ) {
        super();

        this.weeklyPlayers = weeklyPlayers;
        this.players = players;
        this.openInvitations = pending;
        this.containerWidth = width;
        this.containerHeight = height;

        this.scrollContainer = new PIXI.Container();

        const mask = new PIXI.Graphics();
        mask.beginFill(0x000000);
        mask.drawRoundedRect(0, 0, width, height, 10);
        this.addChild(mask);
        this.scrollContainer.mask = mask;

        const highlight = new PIXI.Sprite(PIXI.utils.TextureCache['commonui/opt-in_icon_rays']);
        highlight.anchor.set(0.5);
        highlight.x = width * 0.15;
        highlight.y = -height * 0.2;
        highlight.scale.set(0);
        this.hightlight = highlight;

        const disclamerEmoji = new Sprite(PIXI.utils.TextureCache['mapui/leaderboard-social-icon']);
        disclamerEmoji.anchor.set(0.5);
        disclamerEmoji.scale.set(0);
        disclamerEmoji.x = width * 0.15;
        disclamerEmoji.y = -height * 0.2;
        this.disclamerEmoji = disclamerEmoji;

        const disclamer = createOutlineText(
            Core.local.getText('disclamerFriends'),
            new Rectangle(0, 0, width * 0.7, height * 0.2),
            'Baloo',
            28,
            30,
            0xffffff,
            0x006073
        );
        disclamer.anchor.set(0.5);
        disclamer.x = width * 0.55;
        disclamer.y = -height * 0.2;
        disclamer.scale.set(0);
        this.disclamer = disclamer;

        this.addChild(highlight, disclamerEmoji, disclamer);

        if (this.weeklyPlayers.length + this.players.length + this.openInvitations.length <= 2) {
            this.emojiplus = new Sprite(PIXI.utils.TextureCache['mapui/leaderboard-social-image']);
            this.emojiplus.anchor.set(0.5);
            this.emojiplus.x = width / 2;
            this.emojiplus.y = height / 2;
            this.scrollContainer.addChild(this.emojiplus);
            this.together = createOutlineText(
                Core.local.getText('nofriends'),
                new Rectangle(0, 0, width * 0.9, height * 0.2),
                'Baloo',
                28,
                30
            );
            this.together.anchor.set(0.5);
            this.together.x = width / 2;
            this.together.y = this.emojiplus.y + this.emojiplus.height * 0.8;
            this.emojiplus.scale.set(0);
            this.together.scale.set(0);
            this.scrollContainer.addChild(this.together);

            TweenMax.to(this.emojiplus.scale, 0.2, {
                x: 1,
                y: 1,
            });

            TweenMax.to(this.together.scale, 0.2, {
                delay: 0.2,
                x: 1,
                y: 1,
            });
        }

        this.createContent(canSendGifts);

        // now create a viewport of the appropriate size
        this.createViewport().catch(err => {
            console.log(['error createviewport in leaderboard', err]);
        });
    }

    /**
     * fancy tween all the standard data in
     */
    public async tweenIn() {
        TweenMax.to(this.disclamerEmoji.scale, 0.2, {
            x: 1,
            y: 1,
            ease: Back.easeOut,
        });
        TweenMax.to(this.hightlight.scale, 0.2, {
            x: 0.6,
            y: 0.6,
        });
        await waitForMs(100);
        TweenMax.to(this.disclamer.scale, 0.2, {
            x: 1,
            y: 1,
            ease: Back.easeOut,
        });

        // await waitForMs(100 + 50 * this.players.length + 300);
        // await this.addContent();
    }

    // use this to add a pending invite to group
    public updateLeaderboard() {
        const inviteBox = new InviteBox(this.containerWidth);
        inviteBox.x = this.containerWidth * 0.975;
        inviteBox.y =
            (this.players.length + this.openInvitations.length) * this.tabHeight +
            this.tabHeight * 0.6;
        inviteBox.scale.set(0);
        this.scrollContainer.addChild(inviteBox);
        this.openInvitations.push({ ts: 12345, id: 'whatever' });
        this.updateViewport();
        TweenMax.to(inviteBox.scale, 0.2, {
            ease: Power1.easeOut,
            x: 1,
            y: 1,
        });
    }

    public cleanUp() {
        this.lBContainer.removeAllListeners();
        TweenMax.killChildTweensOf(this, true);
        this.lBContainer.destroy();
        this.lBContainer = null;
        this.playerTabs.forEach(t => {
            t.destroy({ children: true });
        });
        this.playerTabs = [];
    }

    /**
     * sort the players array
     * then add the players to the leaderboard by creating a container for every user
     * that contains all the visual information needed
     * then adds all pending invitations
     * also adds some fancy animation to it
     */
    private createContent(canSendGifts: boolean) {
        this.tabHeight = this.containerHeight * 0.18;

        this.addChild(this.scrollContainer);

        let yPos: number = 0;

        const weeklyHeader = createPixiText(
            Core.local.getText('WeeklyLeaderboard'),
            new PIXI.Rectangle(0, 0, this.containerWidth, this.tabHeight),
            'Baloo',
            30,
            28,
            0xffffff
        );
        weeklyHeader.x = this.containerWidth * 0.5;
        weeklyHeader.y = yPos + weeklyHeader.height;
        this.scrollContainer.addChild(weeklyHeader);
        yPos += weeklyHeader.height * 1.5 + this.tabHeight * 0.1;

        this.weeklyPlayers.sort((a, b) => b.stars - a.stars);
        this.weeklyPlayers.forEach((player, index) => {
            const playerTab = new LeaderboardFriendTab(
                player,
                index,
                this.containerWidth,
                this.containerHeight * 0.15,
                canSendGifts
            );
            playerTab.x = this.containerWidth * 0.5;
            playerTab.y = yPos;
            playerTab.scale.set(1);
            this.scrollContainer.addChild(playerTab);
            this.playerTabs.push(playerTab);

            yPos += this.tabHeight;
        });

        const allTimeHeader = createPixiText(
            Core.local.getText('AllTimeLeaderboard'),
            new PIXI.Rectangle(0, 0, this.containerWidth, this.tabHeight),
            'Baloo',
            30,
            28,
            0xffffff
        );
        allTimeHeader.x = this.containerWidth * 0.5;
        allTimeHeader.y = yPos + this.tabHeight * 0.1;
        this.scrollContainer.addChild(allTimeHeader);
        yPos += allTimeHeader.height + this.tabHeight * 0.1;

        this.players.sort((a, b) => b.stars - a.stars);
        this.players.forEach((player, index) => {
            const playerTab = new LeaderboardFriendTab(
                player,
                index,
                this.containerWidth,
                this.containerHeight * 0.15,
                canSendGifts
            );
            playerTab.x = this.containerWidth * 0.5;
            playerTab.y = yPos;
            playerTab.scale.set(1);
            this.scrollContainer.addChild(playerTab);
            this.playerTabs.push(playerTab);

            yPos += this.tabHeight;
        });
    }

    private async createViewport() {
        // we cannot use viewport here and then not remove it ever...cleanup function needed for when popup is removed
        const itemsCount =
            this.players.length + this.openInvitations.length + this.weeklyPlayers.length + 1;
        const wHeight =
            itemsCount <= 5
                ? this.containerHeight
                : itemsCount * this.tabHeight + this.tabHeight * 0.1;
        this.lBContainer = new Viewport({
            screenWidth: this.containerWidth,
            screenHeight: this.containerHeight,
            worldWidth: this.containerWidth,
            worldHeight: wHeight,
            interaction: app.renderer.plugins.interaction,
        });
        this.lBContainer.drag().decelerate();
        // set boundaries of scrollable leaderboard
        this.lBContainer.clamp({
            left: true,
            right: true,
            top: true,
            bottom: this.lBContainer.worldHeight,
        });
        this.lBContainer.on('drag-start', () => {
            this.playerTabs.forEach(element => {
                if (!element.getPlayerData().isUser) {
                    element.getCoinsButton().setDraggin(true);
                }
            });
        });

        this.lBContainer.on('drag-end', () => {
            this.playerTabs.forEach(element => {
                if (!element.getPlayerData().isUser) {
                    element.getCoinsButton().setDraggin(false);
                }
            });
        });
        this.addChild(this.lBContainer);
        this.lBContainer.addChild(this.scrollContainer);
        await waitForMs(((this.findUserOnLeaderboard() + 3) * 0.1 + 0.75) * 300);
        const weeklyHeight = (this.weeklyPlayers.length + 1) * this.tabHeight;
        const playerPosition =
            this.findUserOnLeaderboard() * this.tabHeight + this.tabHeight * 0.1 + weeklyHeight;
        this.lBContainer.snap(0, playerPosition, {
            time: 500,
            removeOnComplete: true,
            interrupt: true,
        });
    }

    private updateViewport() {
        this.lBContainer.worldHeight =
            this.players.length + this.openInvitations.length <= 5
                ? this.containerHeight
                : (this.players.length + this.openInvitations.length) * this.tabHeight +
                  this.tabHeight * 0.1;
        this.lBContainer.clamp({
            left: true,
            right: true,
            top: true,
            bottom: this.lBContainer.worldHeight,
        });
    }

    private findUserOnLeaderboard() {
        let counter = this.players.findIndex(p => p.isUser);
        if (counter === -1) counter = 0;
        return counter;
    }

    // @ts-ignore
    private createFakeData(numPlayers: number = 10): ILeaderBoardPlayer[] {
        const data: ILeaderBoardPlayer[] = [];
        while (data.length < numPlayers) {
            data.push({
                name: 'Hans Vader',
                stars: 3,
                isUser: false,
                url: this.players[0].url,
                id: this.players[0].id,
            });
        }
        return data;
    }
}
